// src/views/UserViews/insights/components/MobileVersionCurrency.tsx
//
// Mobile app card: no per-version ranked rows at all (that moved to being firmware-only - see
// `VersionDistributionChart`'s header comment). Instead, one three-segment currency bar per
// platform (iOS/Android), built from `computePlatformCurrency` - mobile has both a
// `latest_app_version_*` and a `minimum_app_version_*` catalog value, so a third "unsupported"
// tier is computable here in a way it is not for firmware (no `minimum_firmware_version`
// exists). Keeps the existing iOS/Android totals line and Unknown-platform footnote, both
// computed internally from `buckets` rather than passed in, so this component owns its own
// display data end-to-end.

import { useMemo } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import { UNKNOWN_VERSION_LABEL, type VersionBucket } from '@/models/Insights/InsightsTypes';
import { sumCountsByGroup } from '@/models/Insights/versionDistribution';
import {
  computePlatformCurrency,
  type PlatformCurrency,
  type VersionCurrencyTier,
} from '@/models/Insights/versionCurrency';
import SectionCard from './SectionCard';

const LOADING_SKELETON_HEIGHT = 320;
const PERCENT_MULTIPLIER = 100;
const BAR_HEIGHT = 8;
const BAR_BORDER_RADIUS = 999;

/** Rendering order for the bar's segments and legend rows - up to date first, then needs
 * update, then unsupported, matching severity. */
const TIER_ORDER: VersionCurrencyTier[] = ['upToDate', 'needsUpdate', 'unsupported'];

const TIER_COLOR: Record<VersionCurrencyTier, string> = {
  upToDate: 'success.main',
  needsUpdate: 'warning.main',
  unsupported: 'error.main',
};

const TIER_LABEL_KEY: Record<VersionCurrencyTier, string> = {
  upToDate: StringKeys.INSIGHTS_VERSION_UP_TO_DATE,
  needsUpdate: StringKeys.INSIGHTS_VERSION_NEEDS_UPDATE,
  unsupported: StringKeys.INSIGHTS_VERSION_UNSUPPORTED,
};

/** Small square swatch preceding each legend row - square (not the donut/currency-bar's
 * circular swatch) per this card's own visual convention. */
function LegendSwatch({ color }: { color: string }) {
  return <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color, flexShrink: 0 }} />;
}

type PlatformCurrencyBarProps = { currency: PlatformCurrency };

/**
 * One platform's three-segment currency bar: a bold heading with the platform total
 * right-aligned, the segmented bar itself, and a legend row per non-zero tier. The bar is
 * built from adjacent `Box` elements with proportional `flexGrow` inside an `overflow: hidden`
 * + `borderRadius: 999` container - a single `LinearProgress` cannot render three
 * differently-colored segments in one bar, and clipping the container (rather than rounding
 * each segment's own corners) naturally rounds only the bar's outer edges while leaving
 * shared inner edges square.
 */
function PlatformCurrencyBar({ currency }: PlatformCurrencyBarProps) {
  const { t } = useTranslation();
  const { platform, total, upToDate, needsUpdate, unsupported } = currency;
  const countByTier: Record<VersionCurrencyTier, number> = { upToDate, needsUpdate, unsupported };

  return (
    <Box data-testid={`mobile-version-currency-${platform}`} sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" fontWeight={700}>
          {platform}
        </Typography>
        <Typography variant="body2" fontWeight={700}>
          {t(StringKeys.INSIGHTS_VERSION_GROUP_DEVICE_COUNT, { deviceCount: total })}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          height: BAR_HEIGHT,
          borderRadius: BAR_BORDER_RADIUS,
          overflow: 'hidden',
          bgcolor: 'grey.100',
        }}
      >
        {TIER_ORDER.map(
          (tier) =>
            countByTier[tier] > 0 && (
              <Box key={tier} sx={{ flexGrow: countByTier[tier], bgcolor: TIER_COLOR[tier] }} />
            ),
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 0.75 }}>
        {TIER_ORDER.filter((tier) => countByTier[tier] > 0).map((tier) => {
          const percent =
            total > 0 ? Math.round((countByTier[tier] / total) * PERCENT_MULTIPLIER) : 0;
          return (
            <Box key={tier} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LegendSwatch color={TIER_COLOR[tier]} />
              <Typography variant="caption" color="text.secondary">
                {t(TIER_LABEL_KEY[tier], { deviceCount: countByTier[tier], percent })}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

type MobileVersionCurrencyProps = {
  buckets: VersionBucket[];
  loading?: boolean;
  error?: Error | null;
  /** Maps platform label (e.g. "iOS"/"Android") to its latest published app version. A
   * platform absent or mapped to null/empty renders no bar for that platform at all -
   * callers must not populate this while the release catalog is loading/failed (see
   * `Insights.tsx`). */
  latestByPlatform: Map<string, string | null>;
  /** Maps platform label to its minimum supported app version - optional per platform; when
   * absent the platform's bar simply has no "unsupported" segment. */
  minimumByPlatform: Map<string, string | null>;
};

export default function MobileVersionCurrency({
  buckets,
  loading = false,
  error = null,
  latestByPlatform,
  minimumByPlatform,
}: MobileVersionCurrencyProps) {
  const { t } = useTranslation();

  const platformCurrencies = useMemo(
    () => computePlatformCurrency(buckets, latestByPlatform, minimumByPlatform),
    [buckets, latestByPlatform, minimumByPlatform],
  );

  const platformCounts = useMemo(() => sumCountsByGroup(buckets), [buckets]);
  const unknownCount = platformCounts.get(UNKNOWN_VERSION_LABEL) ?? 0;

  return (
    <SectionCard title={t(StringKeys.INSIGHTS_MOBILE_VERSION_CHART_TITLE)} headingLevel="h3">
      {loading && (
        <Skeleton
          variant="rectangular"
          height={LOADING_SKELETON_HEIGHT}
          data-testid="version-distribution-loading"
        />
      )}

      {!loading && error && (
        <Typography variant="body2" color="error" data-testid="version-distribution-error">
          {t(StringKeys.INSIGHTS_SECTION_LOAD_ERROR)}
        </Typography>
      )}

      {!loading && !error && (
        <>
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="version-summary-line"
            sx={{ mb: 1.5 }}
          >
            {t(StringKeys.INSIGHTS_MOBILE_PLATFORM_SUMMARY, {
              iosCount: platformCounts.get('iOS') ?? 0,
              androidCount: platformCounts.get('Android') ?? 0,
            })}
          </Typography>

          {platformCurrencies.map((currency) => (
            <PlatformCurrencyBar key={currency.platform} currency={currency} />
          ))}

          {unknownCount > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="version-excluded-group-footnote"
              sx={{ display: 'block', mt: 1 }}
            >
              {t(StringKeys.INSIGHTS_VERSION_UNKNOWN_PLATFORM_FOOTNOTE, {
                deviceCount: unknownCount,
              })}
            </Typography>
          )}
        </>
      )}
    </SectionCard>
  );
}
